import * as PriceQualifier1 from "../models/PriceQualifier1";
import * as Country2 from "../models/Country2";
import * as Tenure1 from "./Tenure1";
import * as ListingDocument2 from "../models/ListingDocument2";
import * as ListingImage3 from "../models/ListingImage3";
import * as Listing9 from "../models/Listing9";
import * as t from "io-ts";
import * as ListingImage6 from "../models/ListingImage6";
import * as ListingAmenity2 from "./ListingAmenity2";
import * as S3BucketUrl from "../models/S3BucketUrl";
import * as ListingImage1 from "./ListingImage1";
import { array, option } from "fp-ts/lib";
import * as ListingDocument6 from "./ListingDocument6";
import * as ListingDocument7 from "./ListingDocument7";
import { pipe } from "fp-ts/lib/function";
import { requireExhaustive } from "../../shared/src/util";
import * as ListingPropertyType1 from "./ListingPropertyType1";
import * as ListingFloorAreaUnits1 from "./ListingFloorAreaUnits1";
import { NOTHING_PUBLISHED, publishedInvestorFigures } from "../listing/investorFigures";

export const marketingStateCodec = t.union([
    t.literal("coming_soon"),
    t.literal("live"),
    t.literal("archived"),
]);

export type TMarketingState = t.TypeOf<typeof marketingStateCodec>;

export const saleStateCodec = t.union([
    t.literal("for_sale"),
    t.literal("under_offer"),
    t.literal("for_auction"),
    t.literal("off_market"),
]);

export type TSaleState = t.TypeOf<typeof saleStateCodec>;

// Amounts are in POUNDS. Every figure here has already passed the publishability gate on
// the server, so the browser is never sent a number we would refuse to show.
export const investorFiguresCodec = t.type({
    potential_rent_pcm: t.union([t.number, t.null]),
    has_strong_rental_return: t.boolean,
    renovation_cost: t.union([t.number, t.null]),
    estimated_resale_price: t.union([t.number, t.null]),
    renovation_uplift: t.union([t.number, t.null]),
});

export type TInvestorFigures = t.TypeOf<typeof investorFiguresCodec>;

export const codec = t.type({
    id: t.string,
    marketing_state: marketingStateCodec,
    sale_state: saleStateCodec,
    is_probate: t.boolean,
    description: t.string,
    price: t.union([t.number, t.null]),
    price_qualifier: t.union([PriceQualifier1.codec, t.null]),
    postcode: t.union([t.string, t.null]),
    county: t.string,
    city_town: t.string,
    country: Country2.codec,
    tenure: Tenure1.codec,
    building_name: t.string,
    building_number: t.string,
    sub_building_name: t.string,
    sub_building_number: t.string,
    street_name: t.string,
    locality: t.string,
    district: t.string,
    images: t.array(ListingImage1.codec),
    documents: t.array(ListingDocument2.codec),
    amenities: t.array(ListingAmenity2.codec),
    latitude: t.union([t.number, t.null]),
    longitude: t.union([t.number, t.null]),
    property_type: ListingPropertyType1.codec,
    bedroom_count: t.union([t.number, t.null]),
    bathroom_count: t.union([t.number, t.null]),
    reception_room_count: t.union([t.number, t.null]),
    approximate_floor_area: t.union([t.number, t.null]),
    approximate_floor_area_units: ListingFloorAreaUnits1.codec,
    investor_figures: investorFiguresCodec,
});

export type T = t.TypeOf<typeof codec>;

export const newDefault = (): T => ({
    id: "",
    marketing_state: "live",
    sale_state: "for_sale",
    is_probate: false,
    description: "",
    price: 0,
    price_qualifier: "fixed_price",
    postcode: "",
    county: "",
    city_town: "",
    country: "england",
    tenure: "freehold",
    building_name: "",
    building_number: "",
    sub_building_name: "",
    sub_building_number: "",
    street_name: "",
    locality: "",
    district: "",
    images: [],
    documents: [],
    amenities: [],
    latitude: null,
    longitude: null,
    property_type: "unknown",
    bedroom_count: null,
    bathroom_count: null,
    reception_room_count: null,
    approximate_floor_area: null,
    approximate_floor_area_units: "square_meters",
    investor_figures: NOTHING_PUBLISHED,
});

export const marketingStateFromStatus = (status: Listing9.T["status"]): TMarketingState => {
    switch (status) {
        case "completed":
        case "lost":
            return "archived";
        case "lead":
        case "prep_valuation":
        case "chase_instruction":
        case "instructed_needs_market_prep":
            return "coming_soon";
        case "instructed_on_the_market":
        case "sstc":
        case "auction":
            return "live";
        default:
            return requireExhaustive(status);
    }
};

export const saleStateFromStatus = (status: Listing9.T["status"]): TSaleState => {
    switch (status) {
        case "instructed_on_the_market":
            return "for_sale";
        case "sstc":
            return "under_offer";
        case "auction":
            return "for_auction";
        case "lead":
        case "prep_valuation":
        case "chase_instruction":
        case "instructed_needs_market_prep":
        case "completed":
        case "lost":
            return "off_market";
        default:
            return requireExhaustive(status);
    }
};

const investorFiguresFromListing9 = (m: Listing9.T): TInvestorFigures =>
    publishedInvestorFigures({
        price: m.price_main_currency,
        estimated_rental_price_pcm: m.estimated_rental_price_pcm_main_currency,
        renovation_cost: m.renovation_cost_main_currency,
        estimated_resale_price: m.estimated_resale_price_main_currency,
        renovation_estimate_verified: m.renovation_estimate_verified_at !== null,
        no_renovation_potential: m.no_renovation_potential,
    });

export const fromListing9AndAccountName = (m: Listing9.T, accountName: string): T => (
    {
        id: m.id,
        marketing_state: marketingStateFromStatus(m.status),
        sale_state: saleStateFromStatus(m.status),
        is_probate: m.reason_for_sale === "probate",
        description: m.description ? m.description : "",
        price: m.price_main_currency,
        price_qualifier: m.price_qualifier,
        building_name: m.building_name,
        building_number: m.building_number,
        sub_building_name: m.sub_building_name,
        sub_building_number: m.sub_building_number,
        street_name: m.street_name,
        postcode: m.postcode,
        locality: m.locality,
        district: m.district,
        county: m.county,
        city_town: m.city_town,
        country: m.country,
        tenure: m.tenure,
        images: marketingStateFromStatus(m.status) === "archived"
            ? []
            : array.map((i: ListingImage6.T) => ListingImage3.fromListingImage6(
                i,
                S3BucketUrl.fromAccountAndBucketNames(accountName, S3BucketUrl.bucketNames.LISTINGS_IMAGES_BUCKET)
            ))(m.images),
        documents: marketingStateFromStatus(m.status) === "archived"
            ? []
            : pipe(
                m.documents,
                array.filterMap<ListingDocument7.T, ListingDocument6.T>((document) =>
                    ListingDocument6.codec.is(document) ? option.some(document) : option.none
                ),
                array.map(ListingDocument2.fromListingDocument6),
            ),
        amenities: array.map(ListingAmenity2.fromListingAmenity3)(m.amenities),
        latitude: m.latitude,
        longitude: m.longitude,
        property_type: m.property_type,
        bedroom_count: m.bedroom_count,
        bathroom_count: m.bathroom_count,
        reception_room_count: m.reception_room_count,
        approximate_floor_area: m.approximate_floor_area,
        approximate_floor_area_units: m.approximate_floor_area_units,
        // Published only once the listing is actually live. An archived one loses its pitch
        // the way it loses its photos above; a coming-soon one must not carry it either,
        // because that page is shared with the SELLER during market prep and these are the
        // buyer's numbers — it would tell them we expect their home to fetch the
        // post-renovation figure while we are listing it at the asking price.
        investor_figures: marketingStateFromStatus(m.status) === "live"
            ? investorFiguresFromListing9(m)
            : NOTHING_PUBLISHED,
    }
);
